import { useState } from "react";
import { useCanAccess, useGetList, useTranslate } from "ra-core";
import { Link, Navigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  ListEmptyState,
  ListErrorState,
  ListRowsSkeleton,
} from "../design/ListStates";
import { formatMxn } from "../quoter/PlanSchedule";
import {
  listTotalsMonths,
  summarizeMonth,
  type MonthlyBreakdownLine,
} from "./expenseReportRules";
import type { ExpenseMonthlyTotal } from "./expenseTypes";

// 12 months x 10 categories x 3 payers fits well inside one page of the view.
const PAGE_SIZE = 1000;

const MONTH_NAMES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

/** `2026-08` -> `agosto 2026`. */
const monthLabel = (month: string): string => {
  const [year, monthNumber] = month.split("-");
  return `${MONTH_NAMES[Number(monthNumber) - 1] ?? monthNumber} ${year}`;
};

const BreakdownTable = ({
  title,
  firstColumn,
  lines,
  testId,
}: {
  title: string;
  firstColumn: string;
  lines: MonthlyBreakdownLine[];
  testId: string;
}) => {
  const translate = useTranslate();
  return (
    <section data-testid={testId} className="space-y-2">
      <h2 className="text-lg font-semibold">{title}</h2>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{firstColumn}</TableHead>
            <TableHead className="text-right">
              {translate("crm.expenses.totals.amount")}
            </TableHead>
            <TableHead className="text-right">
              {translate("crm.expenses.totals.iva")}
            </TableHead>
            <TableHead className="text-right">
              {translate("crm.expenses.totals.count")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={line.key}>
              <TableCell>{line.label}</TableCell>
              <TableCell className="text-right tabular-nums">
                {formatMxn(line.totalAmount)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatMxn(line.totalIva)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {line.count}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
};

const Kpi = ({
  testId,
  label,
  value,
}: {
  testId: string;
  label: string;
  value: string;
}) => (
  <div className="rounded-lg bg-card p-4 shadow-sm">
    <p className="text-sm text-muted-foreground">{label}</p>
    <p
      data-testid={testId}
      className="text-2xl font-bold tabular-nums text-primary"
    >
      {value}
    </p>
  </div>
);

/**
 * Almikia Epic EG (TASK-045): "Totales mensuales" over `expense_monthly_totals`
 * (cancelled expenses excluded by the view). A month selector, the month's
 * total amount / IVA / count and its breakdown by category and by payer. The
 * breakdowns are never ranked and carry no percentage, bar or comparison
 * between people. Gated to the four expense roles (redirects everyone else).
 */
export const ExpenseMonthlyTotals = () => {
  const translate = useTranslate();
  const [chosenMonth, setChosenMonth] = useState<string | undefined>();
  const { canAccess, isPending: isAccessPending } = useCanAccess({
    resource: "expense_monthly_totals",
    action: "list",
  });
  const { data, isPending, error, refetch } = useGetList<
    ExpenseMonthlyTotal & { id: string }
  >(
    "expense_monthly_totals",
    {
      pagination: { page: 1, perPage: PAGE_SIZE },
      sort: { field: "month", order: "DESC" },
    },
    { enabled: canAccess === true },
  );
  if (isAccessPending) return null;
  if (!canAccess) return <Navigate to="/" replace />;

  const months = listTotalsMonths(data ?? []);
  const month =
    chosenMonth && months.includes(chosenMonth) ? chosenMonth : months[0];
  const summary = month
    ? summarizeMonth(data ?? [], month, (paidBy) =>
        translate(`crm.expenses.paid_by_values.${paidBy}`),
      )
    : undefined;

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4">
      <header className="space-y-1">
        <Button asChild variant="ghost" className="-ml-3 min-h-11">
          <Link to="/expenses">{translate("crm.expenses.back_to_list")}</Link>
        </Button>
        <h1 className="text-2xl font-semibold">
          {translate("crm.expenses.totals.title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {translate("crm.expenses.totals.excludes_cancelled")}
        </p>
      </header>
      {error ? (
        <ListErrorState
          message={translate("crm.expenses.totals.error")}
          onRetry={() => refetch()}
        />
      ) : isPending ? (
        <ListRowsSkeleton rows={4} />
      ) : !month || !summary ? (
        <ListEmptyState title={translate("crm.expenses.totals.empty")} />
      ) : (
        <>
          <div className="flex flex-col gap-1">
            <Label htmlFor="expense-totals-month">
              {translate("crm.expenses.filters.month")}
            </Label>
            <select
              id="expense-totals-month"
              className="h-11 w-60 max-w-full rounded-md border bg-background px-3 text-base"
              value={month}
              onChange={(event) => setChosenMonth(event.target.value)}
            >
              {months.map((option) => (
                <option key={option} value={option}>
                  {monthLabel(option)}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Kpi
              testId="expense-month-total"
              label={translate("crm.expenses.totals.total_amount")}
              value={formatMxn(summary.totalAmount)}
            />
            <Kpi
              testId="expense-month-iva"
              label={translate("crm.expenses.totals.total_iva")}
              value={formatMxn(summary.totalIva)}
            />
            <Kpi
              testId="expense-month-count"
              label={translate("crm.expenses.totals.total_count")}
              value={String(summary.count)}
            />
          </div>
          <BreakdownTable
            testId="expense-totals-by-category"
            title={translate("crm.expenses.totals.by_category")}
            firstColumn={translate("crm.expenses.fields.category")}
            lines={summary.byCategory}
          />
          <BreakdownTable
            testId="expense-totals-by-paid-by"
            title={translate("crm.expenses.totals.by_paid_by")}
            firstColumn={translate("crm.expenses.fields.paid_by")}
            lines={summary.byPaidBy}
          />
        </>
      )}
    </div>
  );
};

ExpenseMonthlyTotals.path = "/gastos-totales";
